// Device Manager Component - Shows logged in devices and allows remote logout
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Smartphone, Monitor, Tablet, LogOut, RefreshCw, 
  CheckCircle, AlertTriangle
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const DeviceIcon = ({ type }) => {
  switch (type) {
    case 'mobile':
      return <Smartphone className="w-5 h-5" />;
    case 'tablet':
      return <Tablet className="w-5 h-5" />;
    default:
      return <Monitor className="w-5 h-5" />;
  }
};

// Dialog shown when device limit is exceeded during login
export const DeviceLimitDialog = ({ open, onClose, devices, onLogoutDevice, onRetryLogin }) => {
  const [loggingOut, setLoggingOut] = useState(null);
  const [loggedOutDevice, setLoggedOutDevice] = useState(null);

  const handleLogout = async (deviceId) => {
    setLoggingOut(deviceId);
    try {
      await onLogoutDevice(deviceId);
      setLoggedOutDevice(deviceId);
      toast.success('Device logged out successfully! Logging you in...');
      // Wait a moment then retry login
      setTimeout(() => {
        onClose();
        onRetryLogin();
      }, 1000);
    } catch (err) {
      toast.error('Failed to logout device. Please try again.');
      setLoggingOut(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <DialogTitle>Device Limit Reached</DialogTitle>
              <DialogDescription>
                You can only be logged in on {devices?.length || 3} devices at once.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          <p className="text-sm text-muted-foreground">
            Select a device to log out from to continue:
          </p>
          
          {devices?.map((device) => (
            <Card key={device.device_id} className="hover:bg-muted/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <DeviceIcon type={device.device_type} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{device.device_name}</p>
                      {device.last_active && (
                        <p className="text-xs text-muted-foreground">
                          Last active: {new Date(device.last_active).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant={loggedOutDevice === device.device_id ? "outline" : "destructive"}
                    size="sm"
                    onClick={() => handleLogout(device.device_id)}
                    disabled={loggingOut === device.device_id || loggedOutDevice === device.device_id}
                  >
                    {loggingOut === device.device_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : loggedOutDevice === device.device_id ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                        Logged Out
                      </>
                    ) : (
                      <>
                        <LogOut className="w-4 h-4 mr-1" />
                        Logout
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose} className="w-full">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Full device manager for profile/settings page
const DeviceManager = () => {
  const { token } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deviceLimit, setDeviceLimit] = useState(3);
  const [loggingOut, setLoggingOut] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDevices = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/devices`, { headers });
      setDevices(res.data.devices || []);
      setDeviceLimit(res.data.device_limit || 3);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [token]);

  const handleLogoutDevice = async (deviceId) => {
    setLoggingOut(deviceId);
    try {
      await axios.post(`${API_URL}/auth/devices/logout/${deviceId}`, {}, { headers });
      toast.success('Device logged out successfully');
      fetchDevices();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to logout device';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to logout device');
    } finally {
      setLoggingOut(null);
    }
  };

  const handleLogoutAll = async () => {
    if (!window.confirm('This will log out all other devices. Continue?')) return;
    
    try {
      await axios.post(`${API_URL}/auth/devices/logout-all`, {}, { 
        headers: {
          ...headers,
          'X-Device-ID': localStorage.getItem('device_id')
        }
      });
      toast.success('All other devices logged out');
      fetchDevices();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to logout devices';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to logout devices');
    }
  };

  const currentDeviceId = localStorage.getItem('device_id');

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-20 bg-muted rounded-lg" />
        <div className="h-20 bg-muted rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="device-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Logged In Devices</h3>
          <p className="text-sm text-muted-foreground">
            {devices.length} of {deviceLimit} devices
          </p>
        </div>
        {devices.length > 1 && (
          <Button variant="outline" size="sm" onClick={handleLogoutAll}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout All Others
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {devices.map((device) => {
          const isCurrentDevice = device.device_id === currentDeviceId;
          
          return (
            <Card key={device.device_id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCurrentDevice ? 'bg-green-500/10' : 'bg-muted'
                    }`}>
                      <DeviceIcon type={device.device_type} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{device.device_name}</p>
                        {isCurrentDevice && (
                          <Badge variant="outline" className="text-xs">
                            <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                            This Device
                          </Badge>
                        )}
                      </div>
                      {device.last_active && (
                        <p className="text-xs text-muted-foreground">
                          Last active: {new Date(device.last_active).toLocaleString()}
                        </p>
                      )}
                      {device.location && (
                        <p className="text-xs text-muted-foreground">
                          {device.location}
                        </p>
                      )}
                    </div>
                  </div>
                  {!isCurrentDevice && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLogoutDevice(device.device_id)}
                      disabled={loggingOut === device.device_id}
                      className="text-destructive hover:text-destructive"
                    >
                      {loggingOut === device.device_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <LogOut className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default DeviceManager;
